/**
 * Grid snapping utilities for device placement and drag-to-align.
 *
 * `diagram.position` is a device's **anchor** — the origin PIPE-FLO Desktop
 * draws each symbol around, stored verbatim by the importer
 * (`desktop-json-transformer.ts:415-416` scales Desktop's `loc` with no offset;
 * `shape_graphic.cpp:23` setPos(location()); `shapelib_shape.cpp:69`
 * draw(ctx, loc_, ...)). Konva draws nodes around that same origin
 * (`KonvaDeviceNode.tsx:94` `<Group x={cx} y={cy}>`), so the anchor needs no
 * conversion to render.
 *
 * These helpers used to round-trip through a half-dimension offset, treating
 * the stored point as a top-left. That existed solely because React Flow's
 * `snapToGrid` snapped a node's top-left corner. React Flow — and the
 * `SchematicCanvas` that used it — are gone. The offset survived them, and
 * left every imported device drawn (w/2, h/2) off by a type-dependent amount:
 * masked in orthogonal models, where the router redraws pipes around whatever
 * it finds, and fatal in isometric ones, where the authored geometry is
 * absolute.
 *
 * @module grid-snap
 */
import type { HydraulicSystem } from '@shared/hydraulic'
import { getNodeDimensions } from '../vertex-type-registry'
import { isoLatticeFor, isOnIsoBearing, snapToIsoBearingFrom, snapToIsoLatticeMagnetic, type IsoLattice } from './iso-lattice'
import type { NodeBox, Point } from './orthogonal-router'

/**
 * Devices placed ALONG a pipe run rather than on the grid.
 *
 * Desktop does not snap these — measured on `Chemical Process`, where they sit at
 * arbitrary points on the run. Forcing one onto the lattice drags the pipe with
 * it (an in-line device is a VERTEX, so both adjacent segments follow) and bends
 * two authored runs to "fix" a device that was never broken. F2: **off-lattice
 * points are normal and must never be corrected.**
 *
 * ⚠️ This list is OBSERVATIONAL, not exhaustive — it is what the spec measured on
 * one fixture, intersected with the types Connect actually has. The spec also
 * names `sizing_valve`, which is a Desktop-only type with no Connect equivalent
 * (it arrives as something else; cf. #532). If another in-line type turns up
 * being dragged off its run, add it here rather than reworking the mechanism.
 */
const INLINE_DEVICE_TYPES = new Set(['control_valve', 'curve_loss'])

/**
 * Snap a device's anchor to the nearest grid point.
 *
 * `_type` is retained because 40+ call sites pass it, and because the anchor's
 * grid is a property of the model rather than the device.
 *
 * @param position  The device's anchor
 * @param gridSize  Grid spacing in flow-coordinate units. Defaults to 20.
 */
export function snapCenterToGrid(
  position: { x: number; y: number },
  _type: string,
  gridSize: number = 20,
): { x: number; y: number } {
  return {
    x: Math.round(position.x / gridSize) * gridSize,
    y: Math.round(position.y / gridSize) * gridSize,
  }
}

/**
 * Where a device's anchor lands when it is dropped — THE place to ask.
 *
 * The basis is derived from `system` here, deliberately, exactly as `routeEdge`
 * derives it: a basis-shaped parameter is a thing a caller can pass wrong, and
 * passing it wrong snaps an isometric model onto a square grid SILENTLY. Callers
 * pass the system they already have.
 *
 * Three outcomes, and the middle one is not a bug:
 *  - **Isometric model, grid-placed device** → the nearest point of the model's
 *    own lattice (`g = q·s`, with the recorded parity phase).
 *  - **Isometric model, in-line device** → unchanged. See
 *    {@link INLINE_DEVICE_TYPES}: these live on the pipe, not the grid.
 *  - **Everything else** → today's square snap, byte-for-byte. That covers
 *    orthogonal models, Connect-authored models, and isometric models imported
 *    before `import_transform` existed — for those, **a re-import is the fix**.
 *    (Their lattice is not *unknowable*: it is fittable from their own geometry.
 *    See `isoLatticeFor` — we decline to fit because we cannot validate a robust
 *    fit against the 72–77%-on-lattice models where a naive one breaks, not
 *    because the information is gone.)
 */
export function snapAnchor(
  position: Point,
  type: string,
  system: HydraulicSystem | null | undefined,
  gridSize: number = 20,
  vertexId?: string,
): Point {
  if (system) {
    const lattice = isoLatticeFor(system)
    if (lattice) {
      if (INLINE_DEVICE_TYPES.has(type)) return { ...position }
      const hold = vertexId !== undefined ? runsToHold(system, vertexId, lattice) : []
      return snapToIsoLatticeMagnetic(position, lattice, hold)
    }
  }
  return snapCenterToGrid(position, type, gridSize)
}

/**
 * The adjacent point of every run at `vertexId` that is CURRENTLY on an iso
 * bearing — i.e. the ones a drag should try not to break.
 *
 * Reads the vertex's stored position, which during a drag is still the PRE-drag
 * one: the store is only written on drop, so "was this run on-axis before the
 * user grabbed it?" is exactly the question this can answer.
 *
 * ⚠️ Only already-on-bearing runs are held. An authored off-bearing run — an
 * in-line device on a pipe, or a leg the drawing simply has — must not be dragged
 * ONTO the grid by a magnet it never asked for (F2: off-lattice points are normal
 * and must never be corrected).
 *
 * ⚠️ The adjacent POLYLINE point, not the far vertex. A chained run meets this
 * device at `points[0]` (or the last point, if this device is the target); the
 * far vertex is around a corner and its bearing is somebody else's business.
 */
function runsToHold(system: HydraulicSystem, vertexId: string, lattice: IsoLattice): Point[] {
  const self = system.vertices.find(v => v.id === vertexId)
  if (!self) return []
  const byId = new Map(system.vertices.map(v => [v.id, v]))
  const held: Point[] = []

  for (const edge of system.edges) {
    const { source_id, target_id } = edge.connection
    const isSource = source_id === vertexId
    if (!isSource && target_id !== vertexId) continue
    const far = byId.get(isSource ? target_id : source_id)
    if (!far) continue

    const chain = edge.diagram?.points ?? []
    const adjacent = isSource
      ? (chain[0] ?? far.diagram.position)
      : (chain[chain.length - 1] ?? far.diagram.position)

    if (isOnIsoBearing(adjacent, self.diagram.position, lattice)) held.push(adjacent)
  }
  return held
}

/**
 * Place a device so its anchor sits exactly on the target — no rounding, no
 * size term. Used by click-to-drop, where the target is the cursor.
 */
export function centerNodeAt(
  targetCenter: { x: number; y: number },
  _type: string,
): { x: number; y: number } {
  return { x: targetCenter.x, y: targetCenter.y }
}

/**
 * Build the {@link NodeBox} the orthogonal router expects. The box is centred
 * on the anchor; only its half-dimensions depend on the type.
 */
export function vertexToRouteBox(
  position: { x: number; y: number },
  type: string,
): NodeBox {
  const { width, height } = getNodeDimensions(type)
  return {
    center: { x: position.x, y: position.y },
    halfWidth: width / 2,
    halfHeight: height / 2,
  }
}

/**
 * The next interior waypoint to commit while drawing an isometric pipe, or null
 * when drawing does not apply (no source picked yet, or the model has no
 * resolvable lattice — a plain diagonal connect handles that case).
 *
 * The anchor is the last committed waypoint, else the source device's position.
 * The cursor is locked to a bearing and snapped to the lattice. A click that
 * snaps back onto the anchor commits nothing (returns null), so the chain never
 * carries a zero-length run.
 *
 * Pure and system-aware so the gesture's logic is testable without a live stage.
 */
export function nextIsoWaypoint(
  system: HydraulicSystem | null | undefined,
  addPipeSource: string | null,
  draftWaypoints: Point[],
  cursor: Point,
): Point | null {
  if (!system || !addPipeSource) return null
  const lat = isoLatticeFor(system)
  if (!lat) return null
  const anchor = draftWaypoints.length > 0
    ? draftWaypoints[draftWaypoints.length - 1]!
    : system.vertices.find(v => v.id === addPipeSource)?.diagram.position
  if (!anchor) return null
  const snapped = snapToIsoBearingFrom(anchor, cursor, lat)
  if (Math.hypot(snapped.x - anchor.x, snapped.y - anchor.y) < 1e-6) return null
  return snapped
}

/**
 * The COMMITTED portion of an in-progress iso pipe draw, as a flat Konva points
 * array [x0,y0,x1,y1,...] running from the source device through every waypoint
 * committed so far. Empty until at least one waypoint exists (nothing to draw
 * between the source and the cursor yet — that pending segment is drawn
 * separately). Lets the canvas show the growing pipe instead of only the last
 * segment.
 */
export function committedDrawPoints(
  system: HydraulicSystem | null | undefined,
  addPipeSource: string | null,
  draftWaypoints: Point[],
): number[] {
  if (!system || !addPipeSource || draftWaypoints.length === 0) return []
  const src = system.vertices.find(v => v.id === addPipeSource)?.diagram.position
  if (!src) return []
  return [src, ...draftWaypoints].flatMap(p => [p.x, p.y])
}
